// stores/invoiceEntryStore.ts

import { create } from 'zustand';

// Types
export type TransactionType = 'purchase' | 'sales';
export type InvoiceEntryStep = 'upload' | 'extraction' | 'batch-preview' | 'processing' | 'complete' | 'duplicate';

export interface LedgerOption {
  name: string;
  group?: string;
}

export interface StockItemOption {
  name: string;
  group?: string;
  unit?: string;
}

export interface GodownOption {
  name: string;
  parent?: string;
}

export interface VoucherTypeOption {
  name: string;
  parent?: string;
}

export interface VoucherResult {
  success: boolean;
  voucher_number: string | null;
  error: string | null;
}

// Batch processing types
export interface BatchResponse {
  batch_id: string;
  message: string;
  total_count: number;
}

export interface BatchStatus {
  id: string;
  tenant_id: string;
  user_id: string;
  company: string;
  total_count: number;
  extracted_count: number;
  failed_count: number;
  duplicate_count: number;
  posted_count: number;
  status: string;
  created_at: string;
}

export interface InvoiceData {
  invoice_id: string;
  file_name: string;
  file_url?: string | null;
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed' | null;
  extraction_job_id: string | null;
  voucher_status: 'pending' | 'processing' | 'completed' | 'failed' | null;
  voucher_job_id: string | null;
  voucher_number: string | null;
  tool_call_params: ToolCallParams | null;
  error_message: string | null;
  is_duplicate?: boolean;
  // Note: Dropdown data removed - now loaded globally in batchDropdownData
}

export interface InvoiceStatus {
  invoice_id: string;
  batch_id: string;
  file_name: string;
  file_url?: string | null;
  extraction_status: string | null;
  voucher_status: string | null;
  extraction_job_id: string | null;
  voucher_job_id: string | null;
  voucher_number?: string | null;
  error_message: string | null;
  tool_call_params: ToolCallParams | null;
  is_duplicate?: boolean;
  // Note: Dropdown data removed - now loaded globally in batchDropdownData
}

export interface BatchDropdownData {
  ledgers: LedgerOption[];
  stockItems: StockItemOption[];
  godowns: GodownOption[];
  gstLedgers: LedgerOption[];
  expenseLedgers: LedgerOption[];
  transactionAccounts: LedgerOption[];
  voucherTypes: VoucherTypeOption[];
}

// Tool call params - matches backend /api/invoice-entry/* endpoints
export interface ToolCallParams {
  tally_url: string;
  company: string;
  party_name: string;              // RENAMED from supplier_name (generic for both purchase/sales)
  date: string;
  narration: string;
  expense_ledgers: Array<{
    ledger_name: string;
    amount: number;
    is_debit: boolean;
  }>;
  stock_entries?: Array<{
    stock_item: string;
    quantity: number;
    rate: number;
    amount: number;
    unit?: string;
    godown?: string;
    is_inward: boolean;
  }>;
  gst_ledgers?: Array<{
    ledger_name: string;
    amount: number;
    is_debit: boolean;
  }>;
  voucher_type?: string;
  invoice_number?: string;          // RENAMED from supplier_invoice_number (generic)
  invoice_date?: string;            // RENAMED from supplier_invoice_date (generic)
}

interface InvoiceEntryState {
  // Transaction type
  transactionType: TransactionType;

  // Step management
  currentStep: InvoiceEntryStep;

  // Batch management
  batchId: string | null;
  totalCount: number;
  extractedCount: number;
  failedCount: number;
  duplicateCount: number;
  postedCount: number;

  // File state - supports both single and multiple files
  file: File | null; // Keep for backward compatibility
  files: File[]; // Multiple files for batch processing
  fileName: string;
  filePreviewUrl: string | null;

  // Multiple invoices for batch processing
  invoices: InvoiceData[];
  currentInvoiceIndex: number;

  // Company (from tenant context)
  company: string | null;

  // Extraction job
  extractionJobId: string | null;
  extractionProgress: number;
  extractionProgressMessage: string;

  // Tool call params (from LLM)
  toolCallParams: ToolCallParams | null;
  ocrText: string | null;
  matchedVendor: string | null;
  isSuspense: boolean;
  isDuplicate: boolean;
  duplicateDetails: {
    invoice_number: string;
    vendor: string;
    date?: string;
    reference?: string;
  } | null;

  // Global dropdown data (shared by all invoices in batch)
  batchDropdownData: BatchDropdownData | null;

  // Voucher creation job
  voucherJobId: string | null;
  voucherProgress: number;
  voucherProgressMessage: string;

  // Result
  result: VoucherResult | null;

  // Error state
  error: string | null;

  // Loading states
  isLoading: boolean;
}

interface InvoiceEntryActions {
  // Transaction type
  setTransactionType: (type: TransactionType) => void;

  // Step navigation
  setCurrentStep: (step: InvoiceEntryStep) => void;

  // Batch management
  setBatchId: (batchId: string) => void;
  setTotalCount: (count: number) => void;
  setExtractedCount: (count: number) => void;
  setFailedCount: (count: number) => void;
  setDuplicateCount: (count: number) => void;
  setPostedCount: (count: number) => void;

  // File actions - supports both single and multiple files
  setFile: (file: File | null) => void;
  clearFile: () => void;
  setFiles: (files: File[]) => void;
  addFile: (file: File) => void;
  removeFile: (index: number) => void;
  clearFiles: () => void;

  // Invoice management
  setInvoices: (invoices: InvoiceData[]) => void;
  addInvoice: (invoice: InvoiceData) => void;
  updateInvoice: (invoice_id: string, updates: Partial<InvoiceData>) => void;
  setCurrentInvoiceIndex: (index: number) => void;
  goToNextInvoice: () => void;
  goToPreviousInvoice: () => void;
  getCurrentInvoice: () => InvoiceData | null;

  // Company
  setCompany: (company: string) => void;

  // Extraction job
  setExtractionJob: (jobId: string) => void;
  updateExtractionProgress: (progress: number, message: string) => void;
  setFilePreviewUrl: (url: string) => void;

  // Tool call params
  setToolCallParams: (params: ToolCallParams | null) => void;
  updateToolCallParams: (params: Partial<ToolCallParams>) => void;
  setOcrText: (text: string | null) => void;
  setMatchedVendor: (vendor: string | null) => void;
  setIsSuspense: (isSuspense: boolean) => void;
  setIsDuplicate: (isDuplicate: boolean) => void;
  setDuplicateDetails: (details: InvoiceEntryState['duplicateDetails']) => void;

  // Global dropdown data
  setBatchDropdownData: (data: BatchDropdownData | null) => void;

  // Voucher job
  setVoucherJob: (jobId: string) => void;
  updateVoucherProgress: (progress: number, message: string) => void;

  // Result
  setResult: (result: VoucherResult) => void;

  // Error handling
  setError: (error: string | null) => void;

  // Loading
  setIsLoading: (loading: boolean) => void;

  // Reset
  reset: () => void;
}

const initialState: InvoiceEntryState = {
  transactionType: 'purchase',  // Default to purchase
  currentStep: 'upload',
  batchId: null,
  totalCount: 0,
  extractedCount: 0,
  failedCount: 0,
  duplicateCount: 0,
  postedCount: 0,
  file: null,
  files: [],
  fileName: '',
  filePreviewUrl: null,
  invoices: [],
  currentInvoiceIndex: 0,
  company: null,
  extractionJobId: null,
  extractionProgress: 0,
  extractionProgressMessage: '',
  toolCallParams: null,
  ocrText: null,
  matchedVendor: null,
  isSuspense: false,
  isDuplicate: false,
  duplicateDetails: null,
  batchDropdownData: null,  // Loaded once per batch, shared by all invoices
  voucherJobId: null,
  voucherProgress: 0,
  voucherProgressMessage: '',
  result: null,
  error: null,
  isLoading: false,
};

export const useInvoiceEntryStore = create<InvoiceEntryState & InvoiceEntryActions>((set, get) => ({
  ...initialState,

  // Transaction type
  setTransactionType: (type) => set({ transactionType: type }),

  // Step navigation
  setCurrentStep: (step) => set({ currentStep: step }),

  // Batch management
  setBatchId: (batchId) => set({ batchId }),
  setTotalCount: (count) => set({ totalCount: count }),
  setExtractedCount: (count) => set({ extractedCount: count }),
  setFailedCount: (count) => set({ failedCount: count }),
  setDuplicateCount: (count) => set({ duplicateCount: count }),
  setPostedCount: (count) => set({ postedCount: count }),

  // File actions
  setFile: (file) => {
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      set({
        file,
        fileName: file.name,
        filePreviewUrl: previewUrl,
        error: null
      });
    }
  },

  clearFile: () => {
    const { filePreviewUrl } = get();
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    set({ file: null, fileName: '', filePreviewUrl: null });
  },

  setFiles: (files) => set({ files }),

  addFile: (file) => {
    const { files } = get();
    set({ files: [...files, file] });
  },

  removeFile: (index) => {
    const { files } = get();
    set({ files: files.filter((_, i) => i !== index) });
  },

  clearFiles: () => set({ files: [] }),

  // Invoice management
  setInvoices: (invoices) => set({ invoices }),

  addInvoice: (invoice) => {
    const { invoices } = get();
    set({ invoices: [...invoices, invoice] });
  },

  updateInvoice: (invoice_id, updates) => {
    const { invoices } = get();
    set({
      invoices: invoices.map((inv) =>
        inv.invoice_id === invoice_id ? { ...inv, ...updates } : inv
      ),
    });
  },

  setCurrentInvoiceIndex: (index) => set({ currentInvoiceIndex: index }),

  goToNextInvoice: () => {
    const { currentInvoiceIndex, invoices } = get();
    if (currentInvoiceIndex < invoices.length - 1) {
      set({ currentInvoiceIndex: currentInvoiceIndex + 1 });
    }
  },

  goToPreviousInvoice: () => {
    const { currentInvoiceIndex } = get();
    if (currentInvoiceIndex > 0) {
      set({ currentInvoiceIndex: currentInvoiceIndex - 1 });
    }
  },

  getCurrentInvoice: () => {
    const { invoices, currentInvoiceIndex } = get();
    return invoices[currentInvoiceIndex] || null;
  },

  // Company
  setCompany: (company) => set({ company }),

  // Extraction job
  setExtractionJob: (jobId) => set({
    extractionJobId: jobId,
    extractionProgress: 0,
    extractionProgressMessage: 'Starting extraction...'
  }),

  updateExtractionProgress: (progress, message) => set({
    extractionProgress: progress,
    extractionProgressMessage: message
  }),

  setFilePreviewUrl: (url) => {
    const { filePreviewUrl } = get();
    // Revoke old blob URL if it exists
    if (filePreviewUrl && filePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    set({ filePreviewUrl: url });
  },

  // Tool call params
  setToolCallParams: (params) => set({ toolCallParams: params }),
  updateToolCallParams: (params) => {
    const { toolCallParams } = get();
    if (toolCallParams) {
      set({ toolCallParams: { ...toolCallParams, ...params } });
    }
  },
  setOcrText: (text) => set({ ocrText: text }),
  setMatchedVendor: (vendor) => set({ matchedVendor: vendor }),
  setIsSuspense: (isSuspense) => set({ isSuspense }),
  setIsDuplicate: (isDuplicate) => set({ isDuplicate }),
  setDuplicateDetails: (details) => set({ duplicateDetails: details }),

  // Global dropdown data (loaded once per batch)
  setBatchDropdownData: (data) => set({ batchDropdownData: data }),

  // Voucher job
  setVoucherJob: (jobId) => set({
    voucherJobId: jobId,
    voucherProgress: 0,
    voucherProgressMessage: 'Creating voucher...'
  }),

  updateVoucherProgress: (progress, message) => set({
    voucherProgress: progress,
    voucherProgressMessage: message
  }),

  // Result
  setResult: (result) => set({ result }),

  // Error handling
  setError: (error) => set({ error }),

  // Loading
  setIsLoading: (isLoading) => set({ isLoading }),

  // Reset
  reset: () => {
    const { filePreviewUrl } = get();
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    set(initialState);  // Reset to initial state (transactionType defaults to 'purchase')
  },
}));
